import { AxiosResponse } from 'axios'
import { ActionCreator } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ProjectsApi } from '../../../api/projectApi'
import { TimeTrackingApi } from '../../../api/timeTrackingApi'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { IPagination } from '../../../types/pagination'
import { ITimeTracking } from '../../time-tracking/types/timeTracking'
import { ProjectsState } from '../reducers'
import { IDepartmentTrend, IInsightsDepartmentHourUtilization, ILabelsBulk, IPhasesBulk, IProject, IProjectCostSettings, IProjectDepartmentsChartData, IProjectLabel, IProjectPhase, ITrackedHoursByDep, InsightsViewType, ViewType } from '../types/project'
import { ICostByMonths, ICostsByPhaseChart, IExpendituresByLabels, IExpendituresByTasks, IInsightsDepartmentExpendituresTimeline, IInsightsDepartmentExpendituresTimelineChart, IProjectCostBurndownWidget, IProjectTimeTrackingLabel, IProjectTimeTrackingTask } from '../types/projectActivity'
import { IPaymentsByTimeOverviewChart } from './../../../types/dashboard'
import { ICostBySupplier, ICustomer, IProjectBudgetChanges, IProjectBudgetChangesBulk, IProjectRecruitmentStatusChanges, IProjectRecruitmentStatusChangesBulk } from './../types/project'
import * as constants from './constants'

export type ProjectThunkResult<R> = ThunkAction<Promise<R>, ProjectsState, null, ProjectActions>
export type ProjectThunkDispatchType = ThunkDispatch<ProjectsState, null, ProjectActions>

export interface ProjectHasErrored {
  type: constants.PROJECT_HAS_ERRORED
  hasErrored: boolean
}

export interface SetProjectLoader {
  type: constants.SET_PROJECT_LOADER
  load: boolean
}

export interface GetProject {
  type: constants.GET_PROJECT
  id: string | number
}

export interface GetProjectSuccess {
  type: constants.GET_PROJECT_SUCCESS
  project: IProject
}

export interface CreateProjectSuccess {
  type: constants.CREATE_PROJECT_SUCCESS
  project: IProject
}

export interface UpdateProjectSuccess {
  type: constants.UPDATE_PROJECT_SUCCESS
  project: IProject
}

export interface DeleteProjectSuccess {
  type: constants.DELETE_PROJECT_SUCCESS
  project: IProject
}

export interface SetProjectViewType {
  type: constants.SET_PROJECT_VIEW_TYPE
  viewType: ViewType
}

export interface SelectProject {
  type: constants.SELECT_PROJECT
  project: IProject
}

export interface SetInsightsViewType {
  type: constants.SET_INSIGHTS_VIEW_TYPE
  viewType: InsightsViewType
}

export interface UpdatePhasesSuccess {
  type: constants.UPDATE_PHASES_SUCCESS
  phases: IProjectPhase[]
}

export interface UpdateBudgetChangesSuccess {
  type: constants.UPDATE_BUDGET_CHANGES_SUCCESS
  budgets: IProjectBudgetChanges[]
}

export interface GetBudgetChangesSuccess {
  type: constants.GET_BUDGET_CHANGES_SUCCESS
  budgets: IProjectBudgetChanges[]
}

export interface SetProjectSettingsGeneralForm {
  type: constants.SET_PROJECT_SETTINGS_GENERAL_FORM
  open: boolean
}

export interface UpdateLabelsSuccess {
  type: constants.UPDATE_LABELS_SUCCESS
  labels: IProjectLabel[]
}

export interface UpdateProjectBillingCostSuccess {
  type: constants.UPDATE_PROJECT_BILLING_COSTS_SUCCESS
  project: IProject
}

export interface GetDepartmentCostsChartDataSuccess {
  type: constants.GET_DEPARTMENT_COSTS_CHART_DATA_SUCCESS
  data: IProjectDepartmentsChartData
}

export interface GetDepartmentCostTrendCurveDataSuccess {
  type: constants.GET_DEPARTMENT_COST_TREND_CURVE_DATA_SUCCESS
  depExpenditures: IInsightsDepartmentExpendituresTimelineChart
}

export interface GetDepartmentExpendituresTimelineSuccess {
  type: constants.GET_DEPARTMENT_EXPENDITURES_TIMELINE_SUCCESS
  depExpenditures: IInsightsDepartmentExpendituresTimelineChart
}

export interface GetPaginatedDepartmentExpendituresTimelineSuccess {
  type: constants.GET_PAGINATED_DEPARTMENT_EXPENDITURES_TIMELINES_SUCCESS
  data: IInsightsDepartmentExpendituresTimeline
}

export interface SetPaginatedDepartmentExpendituresTimelineActivePage {
  type: constants.SET_PAGINATED_DEPARTMENT_EXPENDITURES_TIMELINES_ACTIVE_PAGE
  page: number
}

export interface GetProjectExpendituresBurndownSuccess {
  type: constants.GET_PROJECT_EXPENDITURES_BURNDOWN_SUCCESS
  expenditures: IProjectCostBurndownWidget
}

export interface GetProjectTimeTrackingSuccess {
  type: constants.GET_PROJECT_TIME_TRACKING_SUCCESS
  timeTracking: ITimeTracking[]
}

export interface GetPaginatedProjectTimeTrackingSuccess {
  type: constants.GET_PAGINATED_TIME_TRACKING_SUCCESS
  data: IPagination<ITimeTracking[]>
}

export interface SetPaginatedProjectTimeTrackingActivePage {
  type: constants.SET_PAGINATED_TIME_TRACKING_ACTIVE_PAGE
  page: number
}

export interface SearchProjectTimeTracking {
  type: constants.SEARCH_PROJECT_TIME_TRACKING
  queryParams: Record<string, string>
}

export interface GetProjectExpendituresByLabelsSuccess {
  type: constants.GET_EXPENDITURES_BY_LABELS_SUCCESS
  labels: IExpendituresByLabels[]
}

export interface GetProjectExpendituresByTasksSuccess{
  type: constants.GET_EXPENDITURES_BY_TASKS_SUCCESS
  tasks: IExpendituresByTasks[]
}

export interface GetPaginatedProjectExpendituresByTasksSuccess {
  type: constants.GET_PAGINATED_EXPENDITURES_BY_TASKS_SUCCESS
  data: IPagination<IExpendituresByTasks[]>
}

export interface SetPaginatedProjectExpendituresByTasksActivePage {
  type: constants.SET_PAGINATED_EXPENDITURES_BY_TASKS_ACTIVE_PAGE
  page: number
}

export interface GetCostByMonthsSuccess {
  type: constants.GET_COST_BY_MONTHS_SUCCESS
  costs: ICostByMonths[]
}

export interface GetProjectTimeTrackingTasksSuccess{
  type: constants.GET_PROJECT_TIME_TRACKING_TASKS_SUCCESS
  timeTracking: IProjectTimeTrackingTask[]
}

export interface GetPaginatedProjectTimeTrackingTasksSuccess {
  type: constants.GET_PAGINATED_PROJECT_TIME_TRACKING_TASKS_SUCCESS
  data: IPagination<IProjectTimeTrackingTask[]>
}

export interface SetPaginatedProjectTimeTrackingTasksActivePage {
  type: constants.SET_PAGINATED_PROJECT_TIME_TRACKING_TASKS_ACTIVE_PAGE
  page: number
}

export interface GetProjectTimeTrackingLabelsSuccess {
  type: constants.GET_PROJECT_TIME_TRACKING_LABELS_SUCCESS
  labels: IProjectTimeTrackingLabel[]
}

export interface GetPaginatedProjectTimeTrackingLabelsSuccess {
  type: constants.GET_PAGINATED_PROJECT_TIME_TRACKING_LABELS_SUCCESS
  data: IPagination<IProjectTimeTrackingLabel[]>
}

export interface SetPaginatedProjectTimeTrackingLabelsActivePage {
  type: constants.SET_PAGINATED_PROJECT_TIME_TRACKING_LABELS_ACTIVE_PAGE
  page: number
}

export interface GetCostByPhasesSuccess {
  type: constants.GET_COST_BY_PHASES_SUCCESS
  costs: ICostsByPhaseChart
}

export interface SearchDepCostUtilization {
  type: constants.SEARCH_DEPARTMENT_COST_UTILIZATION
  filter: { search: string }
}

export interface SearchExpendituresByTasks {
  type: constants.SEARCH_EXPENDITURES_BY_TASKS
  filter: { search: string }
}

export interface GetDepHoursUtilSuccess {
  type: constants.GET_DEP_HOURS_UTIL_SUCCESS
  departments: IInsightsDepartmentHourUtilization
}

export interface GetDepTrendHoursSuccess {
  type: constants.GET_DEP_TREND_HOURS_SUCCESS
  departments: IDepartmentTrend
}

export interface GetTrackedHoursByDepSuccess {
  type: constants.GET_TRACKED_HOURS_BY_DEP_SUCCESS
  data: ITrackedHoursByDep[]
}

export interface SortTimeTrackingReport {
  type: constants.SORT_TIME_TRACKING_REPORT,
  sort: { name: string, direction: string }
}


export interface GetCostsBySupplierSuccess {
  type: constants.GET_COST_BY_SUPPLIER_SUCCESS
  costs: ICostBySupplier[]
}

export interface SetCostsBySupplier {
  type: constants.SET_COST_BY_SUPPLIER
  value: string
}

export interface GetCustomersSuccess {
  type: constants.GET_CUSTOMERS_SUCCESS
  customers: ICustomer[]
}

export interface SetPaymentsByTimePeriod {
  type: constants.SET_PAYMENTS_BY_TIME_PERIOD
  period: string
}

export interface GetPaymentsByTimeChartDataSuccess {
  type: constants.GET_PAYMENTS_BY_TIME_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface SetYearPaymentsByTime {
  type: constants.SET_YEAR_PAYMENTS_BY_TYPE
  year: number
}

export interface GetPaymentForecastChartDataSuccess {
  type: constants.GET_PAYMENT_FORECAST_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface SetPaymentForecastTimePeriod {
  type: constants.SET_PAYMENT_FORECAST_TIME_PERIOD
  period: string
}

export interface GetReceiptForecastChartDataSuccess {
  type: constants.GET_RECEIPT_FORECAST_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface SetYearPaymentForecast {
  type: constants.SET_YEAR_PAYMENT_FORECAST
  year: number
}

export interface SetReceiptForecastTimePeriod {
  type: constants.SET_RECEIPT_FORECAST_TIME_PERIOD
  period: string
}

export interface SetYearReceiptForecast {
  type: constants.SET_YEAR_RECEIPT_FORECAST
  year: number
}

export interface GetExpenditurePhasingChartDataSuccess {
  type: constants.GET_EXPENDITURE_PHASING_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface SetExpenditurePhasingTimePeriod {
  type: constants.SET_EXPENDITURE_PHASING_TIME_PERIOD
  period: string
}

export interface SetTimePeriodDepCost {
  type: constants.SET_TIME_PERIOD_DEP_COST
  period: string
}

export interface SetYearDepCost {
  type: constants.SET_YEAR_DEP_COST
  year: number
}

export interface SetTypeDepCost {
  type: constants.SET_TYPE_DEP_COST
  selected: string
}

export interface SetYearProjCostDev {
  type: constants.SET_YEAR_PROJ_COST_DEV
  year: number
}

export interface SetTypeProjCostDev {
  type: constants.SET_TYPE_PROJ_COST_DEV
  selected: string
}

export interface SetYearRecruitmentCosts {
  type: constants.SET_YEAR_RECRUITMENT_COSTS
  year: number
}

export interface SetTypeRecruitmentCosts {
  type: constants.SET_TYPE_RECRUITMENT_COSTS
  selected: string
}

export interface SetYearExpenditurePhasing {
  type: constants.SET_YEAR_EXPENDITURE_PHASING
  year: number
}

export interface GetCostDevelopmentChartDataSuccess {
  type: constants.GET_COST_DEVELOPMENT_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface SetCostDevelopmentStatus {
  type: constants.SET_COST_DEVELOPMENT_STATUS
  status: string
}

export interface UpdateRecruitmentStatusChangesSuccess {
  type: constants.UPDATE_RECRUITMENT_STATUS_CHANGES_SUCCESS
  status: IProjectRecruitmentStatusChanges[]
}

export interface GetRecruitmentStatusChangesSuccess {
  type: constants.GET_RECRUITMENT_STATUS_CHANGES_SUCCESS
  status: IProjectRecruitmentStatusChanges[]
}

export interface GetRecruitmentStatusChartDataSuccess {
  type: constants.GET_RECRUITMENT_STATUS_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface SetRecruitmentType {
  type: constants.SET_RECRUITMENT_TYPE
  recruitmentType: 'patients' | 'sites'
}

export interface GetMarginDevelopmentChartDataSuccess {
  type: constants.GET_MARGIN_DEVELOPMENT_CHART_DATA_SUCCESS
  data: IPaymentsByTimeOverviewChart
}

export interface GetRestrictedPhasesSuccess {
  type: constants.GET_RESTRICTED_PHASES_SUCCESS
  phases: number[]
}

export interface SetTypeMarginDevelopment {
  type: constants.SET_TYPE_MARGIN_DEVELOPMENT
  selected: string
}

export interface ExportProjectActivitiesSuccess {
  type: constants.EXPORT_PROJECT_ACTIVITIES_SUCCESS
  document: Blob | AxiosResponse<Blob>
}

export interface ExportProjectDirectCostsSuccess {
  type: constants.EXPORT_PROJECT_DIRECT_COSTS_SUCCESS
  document: Blob | AxiosResponse<Blob>
}

export interface CleanProjectState {
  type: constants.CLEAN_PROJECT_STATE
}

export const projectHasErrored = (hasErrored: boolean, error: Error, action?: string): ProjectHasErrored => {
  if (error && action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.PROJECT_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const setProjectLoader = (load: boolean): SetProjectLoader => {
  return {
    type: constants.SET_PROJECT_LOADER,
    load
  }
}

export const getProject: ActionCreator<ProjectThunkResult<IProject>>
  = (id: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IProject> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProject(id, queryParams)
        .then(response => {
          dispatch(getProjectSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PROJECT))
          return {} as IProject
        })
    }
  }

export const getProjectSuccess = (project: IProject): GetProjectSuccess => {
  return {
    type: constants.GET_PROJECT_SUCCESS,
    project
  }
}

export const createProject: ActionCreator<ProjectThunkResult<IProject>>
  = (project: IProject): ProjectThunkResult<IProject> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.createProject(project)
        .then(response => {
          dispatch(createProjectSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.CREATE_PROJECT))
          return {} as IProject
        })
    }
  }

export const createProjectSuccess = (project: IProject): CreateProjectSuccess => {
  toastMsg(`Created ${project.name} project successfully.`, constants.CREATE_PROJECT_SUCCESS, 'success')
  return {
    type: constants.CREATE_PROJECT_SUCCESS,
    project
  }
}

export const updateProject: ActionCreator<ProjectThunkResult<IProject>>
  = (project: IProject): ProjectThunkResult<IProject> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.updateProject(project)
        .then(response => {
          dispatch(updateProjectSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.UPDATE_PROJECT))
          return {} as IProject
        })
    }
  }

export const updateProjectSuccess = (project: IProject): UpdateProjectSuccess => {
  toastMsg(`Updated ${project.name} project successfully.`, constants.UPDATE_PROJECT_SUCCESS, 'success')
  return {
    type: constants.UPDATE_PROJECT_SUCCESS,
    project
  }
}

export const removeProject: ActionCreator<ProjectThunkResult<IProject>>
  = (project: IProject): ProjectThunkResult<IProject> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.deleteProject(project)
        .then(response => {

          dispatch(deleteProjectSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.DELETE_PROJECT))
          return {} as IProject
        })
    }
  }

export const deleteProjectSuccess = (project: IProject): DeleteProjectSuccess => {
  toastMsg(`Deleted ${project.name} project successfully.`, constants.DELETE_PROJECT_SUCCESS, 'success')
  return {
    type: constants.DELETE_PROJECT_SUCCESS,
    project
  }
}

export const setProjectViewType = (viewType: ViewType): SetProjectViewType => {
  return {
    type: constants.SET_PROJECT_VIEW_TYPE,
    viewType
  }
}

export const selectProject = (project: IProject): SelectProject => {
  return {
    type: constants.SELECT_PROJECT,
    project
  }
}

export const setInsightsViewType = (viewType: InsightsViewType): SetInsightsViewType => {
  return {
    type: constants.SET_INSIGHTS_VIEW_TYPE,
    viewType
  }
}

export const setProjectSettingsGeneralForm = (open: boolean): SetProjectSettingsGeneralForm => {
  return {
    type: constants.SET_PROJECT_SETTINGS_GENERAL_FORM,
    open
  }
}

export const updatePhases: ActionCreator<ProjectThunkResult<IProjectPhase[]>>
  = (projectId: string | number, phases: IPhasesBulk): ProjectThunkResult<IProjectPhase[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return await ProjectsApi.updateProjectPhases(projectId, phases)
        .then(response => {
          dispatch(updatePhasesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.UPDATE_PHASES))
          return {} as IProjectPhase[]
        })
    }
  }

export const updatePhasesSuccess = (phases: IProjectPhase[]): UpdatePhasesSuccess => {
  toastMsg(`Updated phases successfully.`, constants.UPDATE_PROJECT_SUCCESS, 'success')
  return {
    type: constants.UPDATE_PHASES_SUCCESS,
    phases
  }
}

export const updateLabels: ActionCreator<ProjectThunkResult<IProjectLabel[]>>
  = (projectId: string | number, labels: ILabelsBulk): ProjectThunkResult<IProjectLabel[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.updateProjectLabels(projectId, labels)
        .then(response => {
          dispatch(updateLabelsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.UPDATE_LABELS))
          return {} as IProjectLabel[]
        })
    }
  }

export const updateLabelsSuccess = (labels: IProjectLabel[]): UpdateLabelsSuccess => {
  toastMsg(`Updated labels successfully.`, constants.UPDATE_PROJECT_SUCCESS, 'success')
  return {
    type: constants.UPDATE_LABELS_SUCCESS,
    labels
  }
}

export const updateProjectBillingCost: ActionCreator<ProjectThunkResult<IProject>>
  = (projectId: string | number, projectSettingsCost: IProjectCostSettings): ProjectThunkResult<IProject> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.updateProjectBillingCost(projectId, projectSettingsCost)
        .then(response => {
          dispatch(updateProjectBillingCostSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.UPDATE_PROJECT_BILLING_COSTS))
          return {} as IProject
        })
    }
  }


export const updateProjectBillingCostSuccess = (project: IProject): UpdateProjectBillingCostSuccess => {
  toastMsg(`Updated ${project.name} project successfully.`, constants.UPDATE_PROJECT_BILLING_COSTS_SUCCESS, 'success')
  return {
    type: constants.UPDATE_PROJECT_BILLING_COSTS_SUCCESS,
    project
  }
}

export const getDepartmentCostsChartData: ActionCreator<ProjectThunkResult<IProjectDepartmentsChartData>>
  = (projectId: number | string, queryParams: Record<string, string>): ProjectThunkResult<IProjectDepartmentsChartData> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getDepartmentCostChartData(projectId, queryParams)
        .then(response => {
          dispatch(getDepartmentCostsChartSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_DEPARTMENT_COSTS_CHART_DATA))
          return {} as IProjectDepartmentsChartData
        })
    }
  }

export const getDepartmentCostsChartSuccess = (data: IProjectDepartmentsChartData): GetDepartmentCostsChartDataSuccess => {
  return {
    type: constants.GET_DEPARTMENT_COSTS_CHART_DATA_SUCCESS,
    data
  }
}

export const getDepartmentCostTrendCurveData: ActionCreator<ProjectThunkResult<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline>>
  = (projectId: string | number, queryParams: Record<string, string>): ProjectThunkResult<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getDepartmentExpendituresTimeline(projectId, queryParams)
        .then(response => {
          dispatch(getDepartmentCostTrendCurveDataSuccess(response as IInsightsDepartmentExpendituresTimelineChart))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_DEPARTMENT_COST_TREND_CURVE_DATA))
          return {} as IInsightsDepartmentExpendituresTimelineChart
        })
    }
  }

export const getDepartmentCostTrendCurveDataSuccess = (depExpenditures: IInsightsDepartmentExpendituresTimelineChart): GetDepartmentCostTrendCurveDataSuccess => {
  return {
    type: constants.GET_DEPARTMENT_COST_TREND_CURVE_DATA_SUCCESS,
    depExpenditures
  }
}

export const getDepartmentExpendituresTimeline: ActionCreator<ProjectThunkResult<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline>>
  = (projectId: string | number): ProjectThunkResult<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getDepartmentExpendituresTimeline(projectId)
        .then(response => {
          dispatch(getDepartmentExpendituresTimelineSuccess(response as IInsightsDepartmentExpendituresTimelineChart))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_DEPARTMENT_EXPENDITURES_TIMELINE))
          return {} as IInsightsDepartmentExpendituresTimelineChart
        })
    }
  }

export const getDepartmentExpendituresTimelineSuccess = (depExpenditures: IInsightsDepartmentExpendituresTimelineChart): GetDepartmentExpendituresTimelineSuccess => {
  return {
    type: constants.GET_DEPARTMENT_EXPENDITURES_TIMELINE_SUCCESS,
    depExpenditures
  }
}

export const getPaginatedDepartmentExpendituresTimeline: ActionCreator<ProjectThunkResult<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline>>
  = (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getDepartmentExpendituresTimeline(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedDepartmentExpendituresTimelineSuccess(response as IInsightsDepartmentExpendituresTimeline))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAGINATED_DEPARTMENT_EXPENDITURES_TIMELINE))
          return {} as IInsightsDepartmentExpendituresTimeline
        })
    }
  }

export const getPaginatedDepartmentExpendituresTimelineSuccess = (data: IInsightsDepartmentExpendituresTimeline): GetPaginatedDepartmentExpendituresTimelineSuccess => {
  return {
    type: constants.GET_PAGINATED_DEPARTMENT_EXPENDITURES_TIMELINES_SUCCESS,
    data
  }
}

export const setPaginatedDepartmentExpendituresTimelineActivePage = (page: number): SetPaginatedDepartmentExpendituresTimelineActivePage => {
  return {
    type: constants.SET_PAGINATED_DEPARTMENT_EXPENDITURES_TIMELINES_ACTIVE_PAGE,
    page: page
  }
}

export const getProjectExpendituresBurndown: ActionCreator<ProjectThunkResult<IProjectCostBurndownWidget>>
  = (projectId: string | number): ProjectThunkResult<IProjectCostBurndownWidget> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectExpendituresBurndown(projectId)
        .then(response => {
          dispatch(getProjectExpendituresBurndownSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PROJECT_EXPENDITURES_BURNDOWN))
          return {} as IProjectCostBurndownWidget
        })
    }
  }

export const getProjectExpendituresBurndownSuccess = (expenditures: IProjectCostBurndownWidget): GetProjectExpendituresBurndownSuccess => {
  return {
    type: constants.GET_PROJECT_EXPENDITURES_BURNDOWN_SUCCESS,
    expenditures
  }
}

export const getProjectTimeTracking: ActionCreator<ProjectThunkResult<ITimeTracking[] | IPagination<ITimeTracking[]>>>
  = (projectId: string | number, queryParams?: Record<string, string | boolean>): ProjectThunkResult<ITimeTracking[] | IPagination<ITimeTracking[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return TimeTrackingApi.getProjectTimeTracking(projectId, queryParams)
        .then(response => {
          dispatch(getProjectTimeTrackingSuccess(response as ITimeTracking[]))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PROJECT_TIME_TRACKING))
          return {} as ITimeTracking[]
        })
    }
  }

export const getProjectTimeTrackingSuccess = (timeTracking: ITimeTracking[]): GetProjectTimeTrackingSuccess => {
  return {
    type: constants.GET_PROJECT_TIME_TRACKING_SUCCESS,
    timeTracking
  }
}

export const downloadProjectTimeTrackingCSV: ActionCreator<ProjectThunkResult<void | Blob | AxiosResponse<Blob>>>
  = (queryParams: Record<string, string | boolean | number>): ProjectThunkResult<void | Blob | AxiosResponse<Blob>> => {
  return async () => {
    return TimeTrackingApi.exportProjectTimeSheet(queryParams)
      .then(response => {
        toastMsg("CSV successfully exported", "CSV EXPORT", 'success')
        return response
      }).catch((error: Error) => toastMsg(error.message, "CSV EXPORT", 'error'))
  }
}

export const downloadProjectOfferDirectCostsCSV: ActionCreator<ProjectThunkResult<void | Blob | AxiosResponse<Blob>>>
  = (queryParams: Record<string, string | boolean | number>): ProjectThunkResult<void | Blob | AxiosResponse<Blob>> => {
  return async () => {
    return ProjectsApi.exportProjectOfferDirectCosts(queryParams)
      .then(response => {
        toastMsg("CSV successfully exported", "CSV EXPORT", 'success')
        return response
      }).catch((error: Error) => toastMsg(error.message, "CSV EXPORT", 'error'))
  }
}

export const downloadProjectOfferPassThroughCostsCSV: ActionCreator<ProjectThunkResult<void | Blob | AxiosResponse<Blob>>>
  = (queryParams: Record<string, string | boolean | number>): ProjectThunkResult<void | Blob | AxiosResponse<Blob>> => {
  return async () => {
    return ProjectsApi.exportProjectOfferPassThroughCosts(queryParams)
      .then(response => {
        toastMsg("CSV successfully exported", "CSV EXPORT", 'success')
        return response
      }).catch((error: Error) => toastMsg(error.message, "CSV EXPORT", 'error'))
  }
}

export const getPaginatedProjectTimeTracking: ActionCreator<ProjectThunkResult<ITimeTracking[] | IPagination<ITimeTracking[]>>>
  = (projectId: string | number, queryParams?: Record<string, string | boolean>): ProjectThunkResult<ITimeTracking[] | IPagination<ITimeTracking[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return TimeTrackingApi.getProjectTimeTracking(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedProjectTimeTrackingSuccess(response as IPagination<ITimeTracking[]>))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAGINATED_TIME_TRACKING))
          return {} as IPagination<ITimeTracking[]>
        })
    }
  }

export const getPaginatedProjectTimeTrackingSuccess = (data: IPagination<ITimeTracking[]>): GetPaginatedProjectTimeTrackingSuccess => {
  return {
    type: constants.GET_PAGINATED_TIME_TRACKING_SUCCESS,
    data
  }
}

export const setPaginatedProjectTimeTrackingActivePage = (page: number): SetPaginatedProjectTimeTrackingActivePage => {
  return {
    type: constants.SET_PAGINATED_TIME_TRACKING_ACTIVE_PAGE,
    page: page
  }
}

export const searchProjectTimeTracking = (queryParams: Record<string, string>): SearchProjectTimeTracking => {
  return {
    type: constants.SEARCH_PROJECT_TIME_TRACKING,
    queryParams
  }
}

export const sortTimeTrackingReport = (name: string, direction: string): SortTimeTrackingReport => {
  return {
    type: constants.SORT_TIME_TRACKING_REPORT,
    sort: { name: name, direction: direction }
  }
}


export const getProjectExpendituresByLabels: ActionCreator<ProjectThunkResult<IExpendituresByLabels[]>>
  = (projectId: string | number): ProjectThunkResult<IExpendituresByLabels[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectExpendituresByLabels(projectId)
        .then(response => {
          dispatch(getProjectExpendituresByLabelsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_EXPENDITURES_BY_LABELS))
          return {} as IExpendituresByLabels[]
        })
    }
  }

export const getProjectExpendituresByLabelsSuccess = (labels: IExpendituresByLabels[]): GetProjectExpendituresByLabelsSuccess => {
  return {
    type: constants.GET_EXPENDITURES_BY_LABELS_SUCCESS,
    labels
  }
}

export const getExpendituresByTasks: ActionCreator<ProjectThunkResult<IExpendituresByTasks[] | IPagination<IExpendituresByTasks[]>>>
  = (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IExpendituresByTasks[] | IPagination<IExpendituresByTasks[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectExpendituresByTasks(projectId, queryParams)
        .then(response => {
          dispatch(getExpendituresByTasksSuccess(response as IExpendituresByTasks[]))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_EXPENDITURES_BY_TASKS))
          return {} as IPagination<IExpendituresByTasks[]>
        })
    }
  }

export const getExpendituresByTasksSuccess = (tasks: IExpendituresByTasks[]): GetProjectExpendituresByTasksSuccess => {
  return {
    type: constants.GET_EXPENDITURES_BY_TASKS_SUCCESS,
    tasks
  }
}

export const getPaginatedExpendituresByTasks: ActionCreator<ProjectThunkResult<IExpendituresByTasks[] | IPagination<IExpendituresByTasks[]>>>
  = (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IExpendituresByTasks[] | IPagination<IExpendituresByTasks[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectExpendituresByTasks(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedExpendituresByTasksSuccess(response as IPagination<IExpendituresByTasks[]>))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAGINATED_EXPENDITURES_BY_TASKS))
          return {} as IPagination<IExpendituresByTasks[]>
        })
    }
  }

export const getPaginatedExpendituresByTasksSuccess = (data: IPagination<IExpendituresByTasks[]>): GetPaginatedProjectExpendituresByTasksSuccess => {
  return {
    type: constants.GET_PAGINATED_EXPENDITURES_BY_TASKS_SUCCESS,
    data
  }
}

export const setPaginatedExpendituresByTasksActivePage = (page: number): SetPaginatedProjectExpendituresByTasksActivePage => {
  return {
    type: constants.SET_PAGINATED_EXPENDITURES_BY_TASKS_ACTIVE_PAGE,
    page: page
  }
}

export const getProjectTimeTrackingTasks: ActionCreator<ProjectThunkResult<IProjectTimeTrackingTask[] | IPagination<IProjectTimeTrackingTask[]>>>
  = (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IProjectTimeTrackingTask[] | IPagination<IProjectTimeTrackingTask[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectTimeTrackingTasks(projectId, queryParams)
        .then(response => {
          dispatch(getProjectTimeTrackingTasksSuccess(response as IProjectTimeTrackingTask[]))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PROJECT_TIME_TRACKING_TASKS))
          return {} as IPagination<IProjectTimeTrackingTask[]>
        })
    }
  }

export const getProjectTimeTrackingTasksSuccess = (timeTracking: IProjectTimeTrackingTask[]): GetProjectTimeTrackingTasksSuccess => {
  return {
    type: constants.GET_PROJECT_TIME_TRACKING_TASKS_SUCCESS,
    timeTracking
  }
}

export const getPaginatedProjectTimeTrackingTasks: ActionCreator<ProjectThunkResult<IProjectTimeTrackingTask[] | IPagination<IProjectTimeTrackingTask[]>>>
  = (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IProjectTimeTrackingTask[] | IPagination<IProjectTimeTrackingTask[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectTimeTrackingTasks(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedProjectTimeTrackingTasksSuccess(response as IPagination<IProjectTimeTrackingTask[]>))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAGINATED_PROJECT_TIME_TRACKING_TASKS))
          return {} as IPagination<IProjectTimeTrackingTask[]>
        })
    }
  }

export const getPaginatedProjectTimeTrackingTasksSuccess = (data: IPagination<IProjectTimeTrackingTask[]>): GetPaginatedProjectTimeTrackingTasksSuccess => {
  return {
    type: constants.GET_PAGINATED_PROJECT_TIME_TRACKING_TASKS_SUCCESS,
    data
  }
}

export const setPaginatedProjectTimeTrackingTasksActivePage = (page: number): SetPaginatedProjectTimeTrackingTasksActivePage => {
  return {
    type: constants.SET_PAGINATED_PROJECT_TIME_TRACKING_TASKS_ACTIVE_PAGE,
    page: page
  }
}

export const getProjectTimeTrackingLabels: ActionCreator<ProjectThunkResult<IProjectTimeTrackingLabel[] | IPagination<IProjectTimeTrackingLabel[]>>>
  = (projectId: string | number, queryParams: Record<string, string>): ProjectThunkResult<IProjectTimeTrackingLabel[] | IPagination<IProjectTimeTrackingLabel[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectTimeTrackingLabels(projectId, queryParams)
        .then(response => {
          dispatch(getProjectTimeTrackingLabelsSuccess(response as IProjectTimeTrackingLabel[]))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PROJECT_TIME_TRACKING_TASKS))
          return {} as IProjectTimeTrackingLabel[]
        })
    }
  }

export const getProjectTimeTrackingLabelsSuccess = (labels: IProjectTimeTrackingLabel[]): GetProjectTimeTrackingLabelsSuccess => {
  return {
    type: constants.GET_PROJECT_TIME_TRACKING_LABELS_SUCCESS,
    labels
  }
}

export const getPaginatedProjectTimeTrackingLabels: ActionCreator<ProjectThunkResult<IProjectTimeTrackingLabel[] | IPagination<IProjectTimeTrackingLabel[]>>>
  = (projectId: string | number, queryParams: Record<string, string>): ProjectThunkResult<IProjectTimeTrackingLabel[] | IPagination<IProjectTimeTrackingLabel[]>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectTimeTrackingLabels(projectId, queryParams)
        .then(response => {
          dispatch(getPaginatedProjectTimeTrackingLabelsSuccess(response as IPagination<IProjectTimeTrackingLabel[]>))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAGINATED_PROJECT_TIME_TRACKING_TASKS))
          return {} as IPagination<IProjectTimeTrackingLabel[]>
        })
    }
  }

export const getPaginatedProjectTimeTrackingLabelsSuccess = (data: IPagination<IProjectTimeTrackingLabel[]>): GetPaginatedProjectTimeTrackingLabelsSuccess => {
  return {
    type: constants.GET_PAGINATED_PROJECT_TIME_TRACKING_LABELS_SUCCESS,
    data
  }
}

export const setPaginatedProjectTimeTrackingLabelsActivePage = (page: number): SetPaginatedProjectTimeTrackingLabelsActivePage => {
  return {
    type: constants.SET_PAGINATED_PROJECT_TIME_TRACKING_LABELS_ACTIVE_PAGE,
    page: page
  }
}

export const getCostByMonths: ActionCreator<ProjectThunkResult<ICostByMonths[]>>
= (projectId: string | number): ProjectThunkResult<ICostByMonths[]> => {
  return async (dispatch: ProjectThunkDispatchType) => {
    return ProjectsApi.getCostByMonths(projectId)
      .then(response => {
        dispatch(getCostByMonthsSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(projectHasErrored(true, error.response.data, constants.GET_COST_BY_MONTHS))
        return {} as ICostByMonths[]
      })
  }
}

export const getCostByMonthsSuccess = (costs: ICostByMonths[]): GetCostByMonthsSuccess => {
  return {
    type: constants.GET_COST_BY_MONTHS_SUCCESS,
    costs
  }
}

export const getCostByPhases: ActionCreator<ProjectThunkResult<ICostsByPhaseChart>>
= (projectId: string | number): ProjectThunkResult<ICostsByPhaseChart> => {
  return async (dispatch: ProjectThunkDispatchType) => {
    return ProjectsApi.getCostByPhases(projectId)
      .then(response => {
        dispatch(getCostByPhasesSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(projectHasErrored(true, error.response.data, constants.GET_COST_BY_PHASES))
        return {} as ICostsByPhaseChart
      })
  }
}

export const getCostByPhasesSuccess = (costs: ICostsByPhaseChart): GetCostByPhasesSuccess => {
  return {
    type: constants.GET_COST_BY_PHASES_SUCCESS,
    costs
  }
}

export const searchDepCostUtilization = (filter: { search: string }): SearchDepCostUtilization => {
  return {
    type: constants.SEARCH_DEPARTMENT_COST_UTILIZATION,
    filter
  }
}

export const searchExpendituresByTasks = (filter: { search: string }): SearchExpendituresByTasks => {
  return {
    type: constants.SEARCH_EXPENDITURES_BY_TASKS,
    filter
  }
}

export const getDepHoursUtil: ActionCreator<ProjectThunkResult<IInsightsDepartmentHourUtilization>>
= (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IInsightsDepartmentHourUtilization> => {
  return async (dispatch: ProjectThunkDispatchType) => {
    return ProjectsApi.getDepHoursUtil(projectId, queryParams)
      .then(response => {
        dispatch(getDepHoursUtilSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(projectHasErrored(true, error.response.data, constants.GET_DEP_HOURS_UTIL))
        return {} as IInsightsDepartmentHourUtilization
      })
  }
}

export const getDepHoursUtilSuccess = (departments: IInsightsDepartmentHourUtilization): GetDepHoursUtilSuccess => {
  return {
    type: constants.GET_DEP_HOURS_UTIL_SUCCESS,
    departments
  }
}

export const getDepTrendHours: ActionCreator<ProjectThunkResult<IDepartmentTrend>>
= (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<IDepartmentTrend> => {
  return async (dispatch: ProjectThunkDispatchType) => {
    return ProjectsApi.getDepartmentTrend(projectId, queryParams)
      .then(response => {
        dispatch(getDepTrendSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(projectHasErrored(true, error.response.data, constants.GET_DEP_TREND_HOURS))
        return {} as IDepartmentTrend
      })
  }
}

export const getDepTrendSuccess = (departments: IDepartmentTrend): GetDepTrendHoursSuccess => {
  return {
    type: constants.GET_DEP_TREND_HOURS_SUCCESS,
    departments
  }
}


export const getTrackedHoursByDep: ActionCreator<ProjectThunkResult<ITrackedHoursByDep[]>>
  = (projectId: number | string, queryParams: Record<string, string>): ProjectThunkResult<ITrackedHoursByDep[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getTrackedHoursByDepartment(projectId, queryParams)
        .then(response => {
          dispatch(getTrackedHoursByDepSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_TRACKED_HOURS_BY_DEP))
          return {} as ITrackedHoursByDep[]
        })
    }
  }

export const getTrackedHoursByDepSuccess = (data: ITrackedHoursByDep[]): GetTrackedHoursByDepSuccess => {
  return {
    type: constants.GET_TRACKED_HOURS_BY_DEP_SUCCESS,
    data
  }
}

export const updateBudgetChanges: ActionCreator<ProjectThunkResult<IProjectBudgetChanges[]>>
  = (projectId: string | number, budgets: IProjectBudgetChangesBulk): ProjectThunkResult<IProjectBudgetChanges[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.updateProjectBudgetChanges(projectId, budgets)
        .then(response => {
          dispatch(updateBudgetChangesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.UPDATE_BUDGET_CHANGES))
          return {} as IProjectBudgetChanges[]
        })
    }
  }

export const updateBudgetChangesSuccess = (budgets: IProjectBudgetChanges[]): UpdateBudgetChangesSuccess => {
  toastMsg(`Updated budgets successfully.`, constants.UPDATE_BUDGET_CHANGES_SUCCESS, 'success')
  return {
    type: constants.UPDATE_BUDGET_CHANGES_SUCCESS,
    budgets
  }
}

export const getBudgetChanges: ActionCreator<ProjectThunkResult<IProjectBudgetChanges[]>>
  = (projectId: number | string, queryParams: Record<string, string>): ProjectThunkResult<IProjectBudgetChanges[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectBudgetChanges(projectId, queryParams)
        .then(response => {
          dispatch(getBudgetChangesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_BUDGET_CHANGES))
          return {} as IProjectBudgetChanges[]
        })
    }
  }

export const getBudgetChangesSuccess = (budgets: IProjectBudgetChanges[]): GetBudgetChangesSuccess => {
  return {
    type: constants.GET_BUDGET_CHANGES_SUCCESS,
    budgets
  }
}

export const getCostsBySupplier: ActionCreator<ProjectThunkResult<ICostBySupplier[]>>
  = (projectId: string | number, queryParams?: Record<string, string>): ProjectThunkResult<ICostBySupplier[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getCostsBySupplier(projectId, queryParams)
        .then(response => {
          dispatch(getCostsBySupplierSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_COST_BY_SUPPLIER))
          return {} as ICostBySupplier[]
        })
    }
  }

export const getCostsBySupplierSuccess = (costs: ICostBySupplier[]): GetCostsBySupplierSuccess => {
  return {
    type: constants.GET_COST_BY_SUPPLIER_SUCCESS,
    costs
  }
}

export const setCostsBySupplier = (value: string): SetCostsBySupplier => {
  return {
    type: constants.SET_COST_BY_SUPPLIER,
    value
  }
}

export const getCustomers: ActionCreator<ProjectThunkResult<ICustomer[]>>
  = (): ProjectThunkResult<ICustomer[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getCustomers()
        .then(response => {
          dispatch(getCustomersSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_CUSTOMERS))
          return {} as ICustomer[]
        })
    }
  }

export const getCustomersSuccess = (customers: ICustomer[]): GetCustomersSuccess => {
  return {
    type: constants.GET_CUSTOMERS_SUCCESS,
    customers
  }
}

export const getPaymentsByTimeChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getPaymentsByTimeChartData(prId, queryParams)
        .then(response => {
          dispatch(getPaymentsByTimeChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAYMENTS_BY_TIME_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getPaymentsByTimeChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetPaymentsByTimeChartDataSuccess => {
  return {
    type: constants.GET_PAYMENTS_BY_TIME_CHART_DATA_SUCCESS,
    data
  }
}

export const getPaymentForecastChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getPaymentForecastChartData(prId, queryParams)
        .then(response => {
          dispatch(getPaymentForecastChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAYMENT_FORECAST_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getPaymentForecastChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetPaymentForecastChartDataSuccess => {
  return {
    type: constants.GET_PAYMENT_FORECAST_CHART_DATA_SUCCESS,
    data
  }
}

export const getReceiptForecastChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getReceiptForecastChartData(prId, queryParams)
        .then(response => {
          dispatch(getReceiptForecastChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_PAYMENT_FORECAST_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getReceiptForecastChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetReceiptForecastChartDataSuccess => {
  return {
    type: constants.GET_RECEIPT_FORECAST_CHART_DATA_SUCCESS,
    data
  }
}

export const getExpenditurePhasingChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getExpenditurePhasingChartData(prId, queryParams)
        .then(response => {
          dispatch(getExpenditurePhasingChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_EXPENDITURE_PHASING_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getExpenditurePhasingChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetExpenditurePhasingChartDataSuccess => {
  return {
    type: constants.GET_EXPENDITURE_PHASING_CHART_DATA_SUCCESS,
    data
  }
}

export const getCostDevelopmentChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getCostDevelopmentChartData(prId, queryParams)
        .then(response => {
          dispatch(getCostDevelopmentChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_COST_DEVELOPMENT_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getCostDevelopmentChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetCostDevelopmentChartDataSuccess => {
  return {
    type: constants.GET_COST_DEVELOPMENT_CHART_DATA_SUCCESS,
    data
  }
}

export const setPaymentsByTimePeriod = (period: string): SetPaymentsByTimePeriod => {
  return {
    type: constants.SET_PAYMENTS_BY_TIME_PERIOD,
    period
  }
}

export const setPaymentForecastTimePeriod = (period: string): SetPaymentForecastTimePeriod => {
  return {
    type: constants.SET_PAYMENT_FORECAST_TIME_PERIOD,
    period
  }
}

export const setReceiptForecastTimePeriod = (period: string): SetReceiptForecastTimePeriod => {
  return {
    type: constants.SET_RECEIPT_FORECAST_TIME_PERIOD,
    period
  }
}

export const setExpenditurePhasingTimePeriod = (period: string): SetExpenditurePhasingTimePeriod => {
  return {
    type: constants.SET_EXPENDITURE_PHASING_TIME_PERIOD,
    period
  }
}

export const setTimePeriodDepCost = (period: string): SetTimePeriodDepCost => {
  return {
    type: constants.SET_TIME_PERIOD_DEP_COST,
    period
  }
}

export const setYearDepCost = (year: number): SetYearDepCost => {
  return {
    type: constants.SET_YEAR_DEP_COST,
    year
  }
}

export const setTypeDepCost = (selected: string): SetTypeDepCost => {
  return {
    type: constants.SET_TYPE_DEP_COST,
    selected
  }
}

export const setCostDevelopmentStatus = (status: string): SetCostDevelopmentStatus => {
  return {
    type: constants.SET_COST_DEVELOPMENT_STATUS,
    status
  }
}

export const updateRecruitmentStatusChanges: ActionCreator<ProjectThunkResult<IProjectRecruitmentStatusChanges[]>>
  = (projectId: string | number, status: IProjectRecruitmentStatusChangesBulk): ProjectThunkResult<IProjectRecruitmentStatusChanges[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.updateProjectRecruitmentStatusChanges(projectId, status)
        .then(response => {
          dispatch(updateRecruitmentStatusChangesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.UPDATE_RECRUITMENT_STATUS_CHANGES))
          return {} as IProjectRecruitmentStatusChanges[]
        })
    }
  }

export const updateRecruitmentStatusChangesSuccess = (status: IProjectRecruitmentStatusChanges[]): UpdateRecruitmentStatusChangesSuccess => {
  toastMsg(`Updated recruitment status successfully.`, constants.UPDATE_RECRUITMENT_STATUS_CHANGES_SUCCESS, 'success')
  return {
    type: constants.UPDATE_RECRUITMENT_STATUS_CHANGES_SUCCESS,
    status
  }
}

export const getRecruitmentStatusChanges: ActionCreator<ProjectThunkResult<IProjectRecruitmentStatusChanges[]>>
  = (projectId: number | string, queryParams: Record<string, string>): ProjectThunkResult<IProjectRecruitmentStatusChanges[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectRecruitmentStatusChanges(projectId, queryParams)
        .then(response => {
          dispatch(getRecruitmentStatusChangesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_RECRUITMENT_STATUS_CHANGES))
          return {} as IProjectRecruitmentStatusChanges[]
        })
    }
  }

export const getRecruitmentStatusChangesSuccess = (status: IProjectRecruitmentStatusChanges[]): GetRecruitmentStatusChangesSuccess => {
  return {
    type: constants.GET_RECRUITMENT_STATUS_CHANGES_SUCCESS,
    status
  }
}

export const getRecruitmentCostsChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectRecruitmentStatusChartData(prId, queryParams)
        .then(response => {
          dispatch(getRecruitmentCostsChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_RECRUITMENT_STATUS_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getRecruitmentCostsChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetRecruitmentStatusChartDataSuccess => {
  return {
    type: constants.GET_RECRUITMENT_STATUS_CHART_DATA_SUCCESS,
    data
  }
}

export const setRecruitmentType = (recruitmentType: 'patients' | 'sites'): SetRecruitmentType => {
  return {
    type: constants.SET_RECRUITMENT_TYPE,
    recruitmentType
  }
}

export const getMarginDevelopmentChartData: ActionCreator<ProjectThunkResult<IPaymentsByTimeOverviewChart>>
  = (prId: number, queryParams?: Record<string, string>): ProjectThunkResult<IPaymentsByTimeOverviewChart> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getProjectMarginDevelopmentChartData(prId, queryParams)
        .then(response => {
          dispatch(getMarginDevelopmentChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_MARGIN_DEVELOPMENT_CHART_DATA))
          return {} as IPaymentsByTimeOverviewChart
        })
    }
  }

export const getMarginDevelopmentChartDataSuccess = (data: IPaymentsByTimeOverviewChart): GetMarginDevelopmentChartDataSuccess => {
  return {
    type: constants.GET_MARGIN_DEVELOPMENT_CHART_DATA_SUCCESS,
    data
  }
}

export const setYearProjCostDev = (year: number): SetYearProjCostDev => {
  return {
    type: constants.SET_YEAR_PROJ_COST_DEV,
    year
  }
}

export const setTypeProjCostDev = (selected: string): SetTypeProjCostDev => {
  return {
    type: constants.SET_TYPE_PROJ_COST_DEV,
    selected
  }
}

export const setYearRecruitmentCosts = (year: number): SetYearRecruitmentCosts => {
  return {
    type: constants.SET_YEAR_RECRUITMENT_COSTS,
    year
  }
}

export const setTypeRecruitmentCosts = (selected: string): SetTypeRecruitmentCosts => {
  return {
    type: constants.SET_TYPE_RECRUITMENT_COSTS,
    selected
  }
}

export const setYearExpenditurePhasing = (year: number): SetYearExpenditurePhasing => {
  return {
    type: constants.SET_YEAR_EXPENDITURE_PHASING,
    year
  }
}

export const setYearPaymentsByTime = (year: number): SetYearPaymentsByTime => {
  return {
    type: constants.SET_YEAR_PAYMENTS_BY_TYPE,
    year
  }
}

export const setYearPaymentForecast = (year: number): SetYearPaymentForecast => {
  return {
    type: constants.SET_YEAR_PAYMENT_FORECAST,
    year
  }
}

export const setYearReceiptForecast = (year: number): SetYearReceiptForecast => {
  return {
    type: constants.SET_YEAR_RECEIPT_FORECAST,
    year
  }
}

export const setTypeMarginDevelopment = (selected: string): SetTypeMarginDevelopment => {
  return {
    type: constants.SET_TYPE_MARGIN_DEVELOPMENT,
    selected
  }
}

export const getRestrictedPhases: ActionCreator<ProjectThunkResult<number[]>>
  = (projectId: number): ProjectThunkResult<number[]> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.getRestrictedPhases(projectId)
        .then(response => {
          dispatch(getRestrictedPhasesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.GET_RESTRICTED_PHASES))
          return [] as number[]
        })
    }
  }

export const getRestrictedPhasesSuccess = (phases: number[]): GetRestrictedPhasesSuccess => {
  return {
    type: constants.GET_RESTRICTED_PHASES_SUCCESS,
    phases
  }
}

export const exportProjectActivities: ActionCreator<ProjectThunkResult<Blob | AxiosResponse<Blob>>>
  = (projectId: number, queryParams?: Record<string, string>): ProjectThunkResult<Blob | AxiosResponse<Blob>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.exportProjectActivities(projectId, queryParams)
        .then(response => {
          dispatch(exportProjectActivitiesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.EXPORT_PROJECT_ACTIVITIES))
          return {} as Blob
        })
    }
  }

export const exportProjectActivitiesSuccess = (document: Blob | AxiosResponse<Blob>): ExportProjectActivitiesSuccess => {
  return {
    type: constants.EXPORT_PROJECT_ACTIVITIES_SUCCESS,
    document
  }
}

export const exportProjectDirectCosts: ActionCreator<ProjectThunkResult<Blob | AxiosResponse<Blob>>>
  = (projectId: number, queryParams?: Record<string, string>): ProjectThunkResult<Blob | AxiosResponse<Blob>> => {
    return async (dispatch: ProjectThunkDispatchType) => {
      return ProjectsApi.exportProjectDirectCosts(projectId, queryParams)
        .then(response => {
          dispatch(exportProjectDirectCostsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(projectHasErrored(true, error.response.data, constants.EXPORT_PROJECT_DIRECT_COSTS))
          return {} as Blob
        })
    }
  }

export const exportProjectDirectCostsSuccess = (document: Blob | AxiosResponse<Blob>): ExportProjectDirectCostsSuccess => {
  return {
    type: constants.EXPORT_PROJECT_DIRECT_COSTS_SUCCESS,
    document
  }
}

export const cleanProjectState = (): CleanProjectState => {
  return {
    type: constants.CLEAN_PROJECT_STATE
  }
}

export type ProjectActions
  = GetProjectSuccess
  | CreateProjectSuccess
  | UpdateProjectSuccess
  | SetProjectViewType
  | SelectProject
  | SetInsightsViewType
  | ProjectHasErrored
  | DeleteProjectSuccess
  | UpdatePhasesSuccess
  | SetProjectSettingsGeneralForm
  | UpdateLabelsSuccess
  | UpdateProjectBillingCostSuccess
  | GetDepartmentExpendituresTimelineSuccess
  | GetProjectExpendituresBurndownSuccess
  | GetDepartmentCostsChartDataSuccess
  | GetProjectTimeTrackingSuccess
  | GetPaginatedProjectTimeTrackingSuccess
  | SetPaginatedProjectTimeTrackingActivePage
  | SearchProjectTimeTracking
  | GetProjectExpendituresByLabelsSuccess
  | GetCostByMonthsSuccess
  | SetPaginatedProjectTimeTrackingTasksActivePage
  | GetProjectTimeTrackingLabelsSuccess
  | GetPaginatedProjectTimeTrackingTasksSuccess
  | GetProjectTimeTrackingTasksSuccess
  | GetCostByPhasesSuccess
  | GetProjectExpendituresByTasksSuccess
  | GetPaginatedProjectExpendituresByTasksSuccess
  | SetPaginatedProjectExpendituresByTasksActivePage
  | GetPaginatedDepartmentExpendituresTimelineSuccess
  | SetPaginatedDepartmentExpendituresTimelineActivePage
  | SearchExpendituresByTasks
  | SearchDepCostUtilization
  | GetDepHoursUtilSuccess
  | GetDepartmentCostTrendCurveDataSuccess
  | GetDepTrendHoursSuccess
  | GetTrackedHoursByDepSuccess
  | UpdateBudgetChangesSuccess
  | GetBudgetChangesSuccess
  | SortTimeTrackingReport
  | GetCostsBySupplierSuccess
  | SetCostsBySupplier
  | GetCustomersSuccess
  | GetPaymentsByTimeChartDataSuccess
  | SetPaymentsByTimePeriod
  | GetPaymentForecastChartDataSuccess
  | SetPaymentForecastTimePeriod
  | GetReceiptForecastChartDataSuccess
  | SetReceiptForecastTimePeriod
  | GetExpenditurePhasingChartDataSuccess
  | SetExpenditurePhasingTimePeriod
  | SetTimePeriodDepCost
  | GetCostDevelopmentChartDataSuccess
  | SetCostDevelopmentStatus
  | UpdateRecruitmentStatusChangesSuccess
  | GetRecruitmentStatusChangesSuccess
  | GetRecruitmentStatusChartDataSuccess
  | SetRecruitmentType
  | GetMarginDevelopmentChartDataSuccess
  | GetPaginatedProjectTimeTrackingLabelsSuccess
  | SetPaginatedProjectTimeTrackingLabelsActivePage
  | SetYearDepCost
  | SetTypeDepCost
  | SetTypeProjCostDev
  | SetYearProjCostDev
  | SetYearRecruitmentCosts
  | SetTypeRecruitmentCosts
  | SetYearExpenditurePhasing
  | SetTypeMarginDevelopment
  | SetYearPaymentsByTime
  | SetYearPaymentForecast
  | SetYearReceiptForecast
  | SetProjectLoader
  | GetRestrictedPhasesSuccess
  | ExportProjectActivitiesSuccess
  | ExportProjectDirectCostsSuccess
  | CleanProjectState
